import React, {useCallback, useEffect, useState} from 'react';
import {App, Button, Empty, Input, List, Modal, Popconfirm, Space, Tag, Tooltip, Typography,} from 'antd';
import {
    ArrowDownOutlined,
    ArrowUpOutlined,
    DeleteOutlined,
    EditOutlined,
    FolderOutlined,
    PlusOutlined,
    TagsOutlined,
} from '@ant-design/icons';
import TagGroupService, {TagGroup} from '../../service/tagGroupService';
import TagGroupDetail from './TagGroupDetail';

const { Text } = Typography;

interface TagGroupManagerProps {
    onGroupsChange?: () => void;
}

const TagGroupManager: React.FC<TagGroupManagerProps> = ({ onGroupsChange }) => {
    const { message } = App.useApp();
    const [tagGroups, setTagGroups] = useState<TagGroup[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [isCreateModalVisible, setIsCreateModalVisible] = useState<boolean>(false);
    const [isEditModalVisible, setIsEditModalVisible] = useState<boolean>(false);
    const [newGroupName, setNewGroupName] = useState<string>('');
    const [editingGroup, setEditingGroup] = useState<TagGroup | null>(null);
    const [editGroupName, setEditGroupName] = useState<string>('');
    const [canCreateMore, setCanCreateMore] = useState<boolean>(true);
    const [groupCount, setGroupCount] = useState<number>(0);
    const [maxAllowed, setMaxAllowed] = useState<number>(10);
    const [managingTagsGroup, setManagingTagsGroup] = useState<TagGroup | null>(null);
    const [editingInlineGroupId, setEditingInlineGroupId] = useState<number | null>(null);
    const [inlineEditValue, setInlineEditValue] = useState<string>('');

    const fetchTagGroups = useCallback(async () => {
        setLoading(true);
        try {
            const response = await TagGroupService.getUserTagGroups();
            setTagGroups(response.tag_groups || []);
            setGroupCount(response.total || 0);
        } catch (error: any) {
            message.error(error.message || 'Failed to fetch tag groups');
            setTagGroups([]);
            setGroupCount(0);
        } finally {
            setLoading(false);
        }
    }, [message]);

    const checkCanCreateMore = useCallback(async () => {
        try {
            const response = await TagGroupService.canCreateMoreGroups();
            setCanCreateMore(response.canCreate);
            setGroupCount(response.currentCount);
            setMaxAllowed(response.maxAllowed);
        } catch (error: any) {
            // Silent failure - limit check is not critical
        }
    }, []);

    useEffect(() => {
        fetchTagGroups();
        checkCanCreateMore();
    }, [fetchTagGroups, checkCanCreateMore]);

    const handleCreateGroup = async () => {
        if (!newGroupName.trim()) {
            message.error('Group name is required');
            return;
        }

        if (newGroupName.trim().length > 100) {
            message.error('Group name must not exceed 100 characters');
            return;
        }

        try {
            await TagGroupService.createTagGroup({
                name: newGroupName.trim(),
                display_order: tagGroups.length,
            });
            message.success(`Tag group "${newGroupName}" created successfully`);
            setIsCreateModalVisible(false);
            setNewGroupName('');
            await fetchTagGroups();
            await checkCanCreateMore();
            if (onGroupsChange) onGroupsChange();
            // Notify sidebar to refresh
            window.dispatchEvent(new Event('tagGroupsUpdated'));
        } catch (error: any) {
            message.error(error.message || 'Failed to create tag group');
        }
    };

    const handleEditGroup = async () => {
        if (!editingGroup || !editGroupName.trim()) {
            message.error('Group name is required');
            return;
        }

        if (editGroupName.trim().length > 100) {
            message.error('Group name must not exceed 100 characters');
            return;
        }

        try {
            await TagGroupService.updateTagGroup(editingGroup.id, {
                name: editGroupName.trim(),
            });
            message.success(`Tag group renamed to "${editGroupName}"`);
            setIsEditModalVisible(false);
            setEditingGroup(null);
            setEditGroupName('');
            await fetchTagGroups();
            if (onGroupsChange) onGroupsChange();
            // Notify sidebar to refresh
            window.dispatchEvent(new Event('tagGroupsUpdated'));
        } catch (error: any) {
            message.error(error.message || 'Failed to update tag group');
        }
    };

    const handleDeleteGroup = async (group: TagGroup) => {
        try {
            await TagGroupService.deleteTagGroup(group.id);
            message.success(`Tag group "${group.name}" deleted successfully`);
            await fetchTagGroups();
            await checkCanCreateMore();
            if (onGroupsChange) onGroupsChange();
            // Notify sidebar to refresh
            window.dispatchEvent(new Event('tagGroupsUpdated'));
        } catch (error: any) {
            message.error(error.message || 'Failed to delete tag group');
        }
    };

    const handleMoveUp = async (group: TagGroup, index: number) => {
        if (index === 0) return;

        const newGroups = [...tagGroups];
        [newGroups[index], newGroups[index - 1]] = [newGroups[index - 1], newGroups[index]];

        try {
            // Update display orders
            await Promise.all([
                TagGroupService.reorderTagGroup(newGroups[index].id, { display_order: index }),
                TagGroupService.reorderTagGroup(newGroups[index - 1].id, { display_order: index - 1 }),
            ]);
            setTagGroups(newGroups);
            if (onGroupsChange) onGroupsChange();
            // Notify sidebar to refresh
            window.dispatchEvent(new Event('tagGroupsUpdated'));
        } catch (error: any) {
            message.error(error.message || 'Failed to reorder tag groups');
            await fetchTagGroups();
        }
    };

    const handleMoveDown = async (group: TagGroup, index: number) => {
        if (index === tagGroups.length - 1) return;

        const newGroups = [...tagGroups];
        [newGroups[index], newGroups[index + 1]] = [newGroups[index + 1], newGroups[index]];

        try {
            // Update display orders
            await Promise.all([
                TagGroupService.reorderTagGroup(newGroups[index].id, { display_order: index }),
                TagGroupService.reorderTagGroup(newGroups[index + 1].id, { display_order: index + 1 }),
            ]);
            setTagGroups(newGroups);
            if (onGroupsChange) onGroupsChange();
            // Notify sidebar to refresh
            window.dispatchEvent(new Event('tagGroupsUpdated'));
        } catch (error: any) {
            message.error(error.message || 'Failed to reorder tag groups');
            await fetchTagGroups();
        }
    };

    const openEditModal = (group: TagGroup) => {
        setEditingGroup(group);
        setEditGroupName(group.name);
        setIsEditModalVisible(true);
    };

    const startInlineEdit = (group: TagGroup) => {
        setEditingInlineGroupId(group.id);
        setInlineEditValue(group.name);
    };

    const handleInlineEditSave = async () => {
        if (!editingInlineGroupId || !inlineEditValue.trim()) {
            setEditingInlineGroupId(null);
            return;
        }

        if (inlineEditValue.trim().length > 100) {
            message.error('Group name must not exceed 100 characters');
            return;
        }

        try {
            await TagGroupService.updateTagGroup(editingInlineGroupId, {
                name: inlineEditValue.trim(),
            });
            message.success('Group renamed successfully');
            setEditingInlineGroupId(null);
            setInlineEditValue('');
            await fetchTagGroups();
            if (onGroupsChange) onGroupsChange();
            // Notify sidebar to refresh
            window.dispatchEvent(new Event('tagGroupsUpdated'));
        } catch (error: any) {
            message.error(error.message || 'Failed to rename tag group');
        }
    };


    return (
        <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
                <Space>
                    <FolderOutlined style={{ fontSize: 20, color: '#1890ff' }} />
                    <Text strong style={{ fontSize: 18 }}>Manage Tag Groups</Text>
                    <Tag color="blue" style={{ fontSize: 13 }}>{groupCount} / {maxAllowed}</Tag>
                </Space>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    onClick={() => setIsCreateModalVisible(true)}
                    disabled={!canCreateMore}
                    size="middle"
                >
                    New Group
                </Button>
            </div>

            <div>
            {!canCreateMore && (
                <div style={{ marginBottom: 16 }}>
                    <Text type="warning">
                        You've reached the maximum limit of {maxAllowed} tag groups.
                    </Text>
                </div>
            )}

            {tagGroups.length === 0 ? (
                <Empty
                    description="No tag groups yet"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                >
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={() => setIsCreateModalVisible(true)}
                    >
                        Create Your First Group
                    </Button>
                </Empty>
            ) : (
                <List
                    loading={loading}
                    dataSource={tagGroups}
                    bordered={false}
                    style={{
                        backgroundColor: '#fafafa',
                        borderRadius: 8,
                        padding: '16px'
                    }}
                    renderItem={(group, index) => (
                        <List.Item
                            style={{
                                backgroundColor: 'white',
                                marginBottom: 8,
                                padding: '16px',
                                borderRadius: 6,
                                border: '1px solid #e8e8e8'
                            }}
                            actions={[
                                <Tooltip title="Manage Tags">
                                    <Button
                                        icon={<TagsOutlined />}
                                        size="small"
                                        type="primary"
                                        onClick={() => setManagingTagsGroup(group)}
                                    />
                                </Tooltip>,
                                <Tooltip title="Move Up">
                                    <Button
                                        icon={<ArrowUpOutlined />}
                                        size="small"
                                        disabled={index === 0}
                                        onClick={() => handleMoveUp(group, index)}
                                    />
                                </Tooltip>,
                                <Tooltip title="Move Down">
                                    <Button
                                        icon={<ArrowDownOutlined />}
                                        size="small"
                                        disabled={index === tagGroups.length - 1}
                                        onClick={() => handleMoveDown(group, index)}
                                    />
                                </Tooltip>,
                                <Tooltip title="Rename">
                                    <Button
                                        icon={<EditOutlined />}
                                        size="small"
                                        onClick={() => openEditModal(group)}
                                    />
                                </Tooltip>,
                                <Popconfirm
                                    title="Delete this tag group?"
                                    description={
                                        <>
                                            This will permanently delete the group and all its tag preferences.
                                            <br />
                                            Are you sure?
                                        </>
                                    }
                                    onConfirm={() => handleDeleteGroup(group)}
                                    okText="Delete"
                                    cancelText="Cancel"
                                    okButtonProps={{ danger: true }}
                                >
                                    <Tooltip title="Delete">
                                        <Button
                                            icon={<DeleteOutlined />}
                                            size="small"
                                            danger
                                        />
                                    </Tooltip>
                                </Popconfirm>,
                            ]}
                        >
                            <List.Item.Meta
                                avatar={<FolderOutlined style={{ fontSize: 24, color: '#1890ff' }} />}
                                title={
                                    editingInlineGroupId === group.id ? (
                                        <Input
                                            value={inlineEditValue}
                                            onChange={(e) => setInlineEditValue(e.target.value)}
                                            onPressEnter={handleInlineEditSave}
                                            onBlur={handleInlineEditSave}
                                            autoFocus
                                            maxLength={100}
                                            style={{ width: '300px' }}
                                            placeholder="Group name"
                                        />
                                    ) : (
                                        <Text
                                            strong
                                            onClick={() => startInlineEdit(group)}
                                            style={{ cursor: 'pointer' }}
                                            title="Click to edit"
                                        >
                                            {group.name}
                                        </Text>
                                    )
                                }
                                description={
                                    <Text type="secondary">
                                        Created {new Date(group.created_at).toLocaleDateString()}
                                    </Text>
                                }
                            />
                        </List.Item>
                    )}
                />
            )}

            {/* Create Group Modal */}
            <Modal
                title="Create New Tag Group"
                open={isCreateModalVisible}
                onOk={handleCreateGroup}
                onCancel={() => {
                    setIsCreateModalVisible(false);
                    setNewGroupName('');
                }}
                okText="Create"
            >
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Text>Enter a name for your new tag group:</Text>
                    <Input
                        placeholder="e.g., Gaming News, Work Tech, Entertainment"
                        value={newGroupName}
                        onChange={(e) => setNewGroupName(e.target.value)}
                        onPressEnter={handleCreateGroup}
                        maxLength={100}
                        showCount
                        autoFocus
                    />
                </Space>
            </Modal>

            {/* Edit Group Modal */}
            <Modal
                title="Rename Tag Group"
                open={isEditModalVisible}
                onOk={handleEditGroup}
                onCancel={() => {
                    setIsEditModalVisible(false);
                    setEditingGroup(null);
                    setEditGroupName('');
                }}
                okText="Save"
            >
                <Space direction="vertical" style={{ width: '100%' }}>
                    <Text>Enter a new name for this tag group:</Text>
                    <Input
                        placeholder="Group name"
                        value={editGroupName}
                        onChange={(e) => setEditGroupName(e.target.value)}
                        onPressEnter={handleEditGroup}
                        maxLength={100}
                        showCount
                        autoFocus
                    />
                </Space>
            </Modal>

            {/* Manage Tags Modal */}
            <Modal
                title={
                    <Space>
                        <TagsOutlined style={{ color: '#1890ff' }} />
                        <span>Manage Tags - {managingTagsGroup?.name}</span>
                    </Space>
                }
                open={!!managingTagsGroup}
                onCancel={() => {
                    setManagingTagsGroup(null);
                    // Refresh groups and notify parent when modal closes
                    fetchTagGroups();
                    if (onGroupsChange) onGroupsChange();
                }}
                footer={null}
                width={800}
                destroyOnClose
            >
                {managingTagsGroup && (
                    <TagGroupDetail
                        groupId={managingTagsGroup.id}
                        groupName={managingTagsGroup.name}
                        onTagsChange={() => {
                            // Refresh local groups list but don't notify parent
                            // This keeps the modal open while showing updated data
                            fetchTagGroups();
                        }}
                    />
                )}
            </Modal>
            </div>
        </div>
    );
};

export default TagGroupManager;
